import axios from "axios";
import { BillingInterval, Subscription } from "./subscription";
import { TimeTrackerProvider } from "@/views";

const baseUrl = "/api/v0/admin/accounts";
const plansUrl = "/api/v0/admin/plans";

export interface Plan {
    id: string;
    title: string;
    price: number;
    interval: BillingInterval;
    trialDays: number;
    isActive: boolean;
    sortOrder: number;
    subscribers: number;
}

export type PlanEdit = Omit<Plan, "id" | "subscribers">;

export interface AdminAccount {
    id: string;
    name: string;
    created: string;
    providers: TimeTrackerProvider[];
    ownerName: string | null;
    ownerEmail: string | null;
    requestCount: number;
    subscription: Subscription;
}

const adminApi = {
    getAccounts: async (): Promise<AdminAccount[]> => {
        const { data } = await axios.get<AdminAccount[]>(baseUrl);
        return data;
    },

    grantComplimentary: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.post<Subscription>(`${baseUrl}/${accountId}/comp`);
        return data;
    },

    revokeComplimentary: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.delete<Subscription>(`${baseUrl}/${accountId}/comp`);
        return data;
    },

    setGrace: async (accountId: string, until: string): Promise<Subscription> => {
        const { data } = await axios.put<Subscription>(`${baseUrl}/${accountId}/grace`, { until });
        return data;
    },

    clearGrace: async (accountId: string): Promise<Subscription> => {
        const { data } = await axios.delete<Subscription>(`${baseUrl}/${accountId}/grace`);
        return data;
    },

    extendTrial: async (accountId: string, days: number): Promise<Subscription> => {
        const { data } = await axios.post<Subscription>(`${baseUrl}/${accountId}/extend-trial`, { days });
        return data;
    },

    getPlans: async (): Promise<Plan[]> => {
        const { data } = await axios.get<Plan[]>(plansUrl);
        return data;
    },

    createPlan: async (plan: PlanEdit): Promise<Plan> => {
        const { data } = await axios.post<Plan>(plansUrl, plan);
        return data;
    },

    updatePlan: async (id: string, plan: PlanEdit): Promise<Plan> => {
        const { data } = await axios.put<Plan>(`${plansUrl}/${id}`, plan);
        return data;
    },

    deletePlan: async (id: string): Promise<void> => {
        await axios.delete(`${plansUrl}/${id}`);
    },
};

export default adminApi;
